const WTF = (() => {
  'use strict';

  // Constants
  const RE_QUOTE = /"([^"]+)"/gi;
  const RE_JSON = /\.json$/i;
  const RE_COL = /^gsx\$(.+)$/i;
  const RE_KEY = /[a-z0-9_-]{32,}/i;
  const DOCS_PATH = "https://spreadsheets.google.com/feeds/list/{key}/od6/public/values?alt=json";

  // Variables
  let templates;
  let responses;
  let headings;
  let corpus;
  let regex;
  let dom;

  /**
   * Called once initialization is finished
   */
  function start() {
    // Copy out templates then remove from corpus
    templates = corpus.template;
    responses = corpus.response;
    headings = corpus.heading;

    delete corpus.template;
    delete corpus.response;
    delete corpus.heading;

    // Enable UI and generate first idea
    initUI();
    buildRegExp();
    generate();

    const templatesEl = document.getElementById('templates');
    const outcomesEl = document.getElementById('outcomes');

    if (templatesEl) {
      templatesEl.textContent = templates.length + ' template' + (templates.length === 1 ? '' : 's');
    }
    if (outcomesEl) {
      outcomesEl.textContent = getTotalNumOptions().toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    }
  }

  function getTotalNumOptions() {
    let num = 0;
    for (let i = 0; i < templates.length; i++) {
      num += getNumTemplateOptions(templates[i]);
    }
    return num;
  }

  function getNumTemplateOptions(template) {
    let iter = 0; // Safety mechanism
    let item = regex.exec(template);

    let num = 0;
    while (item && ++iter < 1000) {
      const type = item[0].substr(1);
      const typeNum = corpus[type].length;
      if (num === 0) {
        num = corpus[type].length;
      } else {
        num = num * corpus[type].length;
      }
      item = regex.exec(template);
    }
    return num;
  }

  /**
   * Converts CSV to a regular corpus object
   */
  function parseCSV(csv) {
    const rows = csv.split('\n');
    const keys = {};
    const data = {};

    for (let i = 0; i < rows.length; i++) {
      const j = i - 1;
      const cols = rows[i].replace(RE_QUOTE, escape).split(',');

      for (let k = 0; k < cols.length; k++) {
        if (i === 0) {
          data[keys[k] = cols[k].toLowerCase()] = [];
        } else if (cols[k]) {
          data[keys[k]][j] = unescape(cols[k]).replace(/^"|"$/g, '');
        }
      }
    }

    return data;
  }

  /**
   * Converts JSON data to a regular corpus object
   */
  function parseJSON(json) {
    const map = {};
    const keys = {};
    const data = {};
    const rows = json.feed.entry;

    for (const key in rows[0]) {
      if (RE_COL.test(key)) {
        map[key] = key.match(RE_COL)[1].toLowerCase();
        keys[key] = [];
      }
    }

    for (const key in keys) {
      data[map[key]] = keys[key];

      for (let i = 0; i < rows.length; i++) {
        const val = rows[i][key].$t;

        if (val && val.length) {
          keys[key].push(val);
        }
      }
    }

    return data;
  }

  /**
   * Binds event handlers to control the interface
   */
  function initUI() {
    const loadingEl = document.querySelector('.loading');
    if (loadingEl) {
      loadingEl.remove();
    }

    dom = {
      generate: document.getElementById('generate'),
      output: document.getElementById('output')
    };

    dom.generate.addEventListener('click', (e) => {
      e.preventDefault();
      generate();
    });

    // Add keyboard support
    dom.generate.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        generate();
      }
    });
  }

  /**
   * Builds a regular expression for all types in the corpus
   */
  function buildRegExp() {
    const types = Object.keys(corpus).sort((a, b) => {
      if (a.length === b.length) return 0;
      return a.length > b.length ? -1 : 1;
    });

    const content = '@(type)'.replace('type', types.join('|'));
    regex = new RegExp(content, 'gi');
  }

  /**
   * Generates ideas based on the corpus
   */
  function generate() {
    let iter = 0; // Safety mechanism
    let idea = randomItem(templates);
    let item = regex.exec(idea);
    const copy = cloneCorpus();

    while (item && ++iter < 1000) {
      const type = item[0];
      const text = item[1];

      const part = randomItem(copy[text], true);
      idea = idea.replace(type, part);

      regex.lastIndex = 0;
      item = regex.exec(idea);
    }

    // Update output
    dom.generate.textContent = randomItem(responses);
    dom.output.innerHTML = `
      <dl>
        <dt>${randomItem(headings)}</dt>
        <dd>${idea}</dd>
      </dl>
    `;

    // Toggle animation
    setTimeout(showOutput, 0);
    hideOutput();
  }

  function hideOutput() {
    dom.output.classList.remove('animate');
    dom.output.style.opacity = '0';
  }

  function showOutput() {
    dom.output.classList.add('animate');
    dom.output.style.opacity = '1';
  }

  function randomItem(list, remove = false) {
    const index = Math.floor(Math.random() * list.length);
    const item = list[index];

    if (remove) {
      list.splice(index, 1);
    }

    return item;
  }

  function cloneCorpus() {
    const copy = {};
    for (const key in corpus) {
      copy[key] = [...corpus[key]];
    }
    return copy;
  }

  /**
   * Public API
   */
  return {
    /**
     * Initialize WTF Engine
     * @param {Object|string} data - Corpus object, JSON path, or Google Spreadsheet key
     */
    init(data) {
      if (!data) throw new Error(`${data} is not a valid corpus`);

      if (typeof data === 'string') {
        if (RE_JSON.test(data)) {
          // JSON file
          fetch(data)
            .then(response => response.json())
            .then(jsonData => {
              corpus = jsonData;
              start();
            })
            .catch(error => {
              throw new Error(`Cannot load JSON data: ${error}`);
            });
        } else if (RE_KEY.test(data)) {
          // Google Spreadsheet
          fetch(DOCS_PATH.replace('{key}', data))
            .then(response => response.json())
            .then(jsonData => {
              corpus = parseJSON(jsonData);
              start();
            })
            .catch(error => {
              throw new Error('Cannot load spreadsheet. Is it published? (@see https://support.google.com/drive/answer/37579?hl=en)');
            });
        } else {
          throw new Error(`Unrecognised data format: ${data}`);
        }
      } else if (typeof data === 'object') {
        // Object
        corpus = data;
        start();
      }
    },

    // Expose generate method
    generate
  };
})();

export default WTF;
